import $ from 'jquery';
import 'jquery-trend';
import $script from 'scriptjs';
import Asyncview from 'shopify-asyncview';
import morphdom from 'morphdom';
import rimg from '@pixelunion/rimg-shopify';

import CartMini from '../components/CartMini';

/**
 * _getItemKey
 *
 * Checks a target and recursively checks parents until an element with a data-cart-item-key
 * is found, when the value of that attribute is returned.
 *
 * @param {HTMLElement} target Element from which to start search for item key (inclusive)
 */
function _getItemKey(target) {
  if (!target) {
    return null;
  }

  if (target.hasAttribute('data-cart-item-key')) {
    return target.dataset.cartItemKey;
  }

  return _getItemKey(target.parentNode);
}

export default class StaticCart {
  constructor(section) {
    this.el = section.el;
    this.$el = $(this.el);
    this.settings = section.data.settings;
    this.events = [];

    this.cartForm = section.el.querySelector('[data-cart-form]');
    this.$shippingStrings = $('[data-shipping-calculator-strings]', this.el);
    this.shippingContext = {};

    this.cartMini = new CartMini();

    const $scripts = $('[data-scripts]');

    $script([
      $scripts.data('shopify-common'),
      $scripts.data('shopify-api-url'),
    ], () => {
      $script($scripts.data('shopify-countries'), () => {
        $script($scripts.data('shopify-cart'), () => {
          Shopify.onError = XMLHttpRequest => this.handleErrors(XMLHttpRequest);
          this.events.push(
            $('.instructions textarea', this.el).on('change.cart', this.saveNote.bind(this)),
          );

          if (this.cartForm) {
            this.cartTable = this.cartForm.querySelector('[data-cart-table]');
            this.cartDiscounts = this.cartForm.querySelector('[data-cart-discounts]');
            this.totalPrice = this.cartForm.querySelector('[data-cart-total]');

            this._handleClick = this._handleClick.bind(this);
            this._handleQtyChange = this._handleQtyChange.bind(this);
            this._handleQtyKeydown = this._handleQtyKeydown.bind(this);

            this.cartForm.addEventListener('click', this._handleClick);
            this.cartForm.addEventListener('change', this._handleQtyChange);
            this.cartForm.addEventListener('keydown', this._handleQtyKeydown);

            this.updateTimeout = null;
          }

          if (this.settings.shipping && this.$shippingStrings.length) {
            this.countryProvinceSelector = new Shopify.CountryProvinceSelector(
              'address_country',
              'address_province',
              {
                hideElement: 'address_province_container',
              },
            );

            try {
              this.shippingContext = JSON.parse(this.$shippingStrings.text());
            } catch (error) {
              console.log('No shipping localisations found, unable to continue.');
            }

            if (this.shippingContext == null) { return;}

            this.events.push(
              $('[data-shipping-calculator-submit]', this.el).on('click.cart', this.calculateShipping.bind(this)),
              $('#address_zip', this.el).on('keydown.cart', this.calculateShipping.bind(this)),
            );


            if ($('[data-cart-item]', this.el).length) {
              this.shippingCalculator();
            }
          }
        });
      });
    });
  }

  /**
   * Gets the current value of the quantity input box for a given line item key
   *
   * @param {string} key
   */
  _getItemQuantity(key) {
    return parseInt(
      this.cartForm
      .querySelector(`[data-cart-item-key="${key}"] [data-cart-item-quantity]`)
      .value,
      10,
    );
  }

  /**
   * Sets a value for the quantity input box for a given line item key
   *
   * @param {string} key
   *
   * @param {integer} quantity
   */
  _setItemQuantity(key, quantity) {
    this.cartForm
      .querySelector(`[data-cart-item-key="${key}"] [data-cart-item-quantity]`)
      .value = quantity;
  }

  _handleClick(event) {
    if (!event.target.hasAttribute('data-cart-action')) {
      return;
    }

    const { cartAction } = event.target.dataset;
    const key = _getItemKey(event.target);

    // Switch is a bit redundant, but leaving as is to match other themes and make
    // future additions easy.
    switch (cartAction) {
      case 'remove':
        event.preventDefault();
        this._setItemQuantity(key, 0);
        this._updateItem(key, 0);
        break;

      default:
        // do nothing
        break;
    }
  }

  _handleQtyChange(event) {
    if (!event.target.hasAttribute('data-cart-item-quantity')) {
      return;
    }

    const key = _getItemKey(event.target);
    const quantity = this._getItemQuantity(key);
    this._updateItem(key, quantity);
  }

  _handleQtyKeydown(event) {
    if (event.target.hasAttribute('data-cart-item-quantity') && event.key === 'Enter') {
      event.preventDefault();
      this._handleQtyChange(event);
    }
  }

  /**
   * Sends a request to the server to update a single product
   *
   * @param {string} key Shopify line item key (or product ID)
   *
   * @param {integer} quantity New quantity for item. If 0, item will be removed from cart.
   * @private
   */
  _updateItem(key, quantity) {
    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      if (this._getItemQuantity(key) !== quantity) {
        this.updateTimeout = null;
        return;
      }

      const thisTimeoutId = this.updateTimeout;

      $.ajax({
        type: 'POST',
        url: `${window.Theme.routes.cart_change_url}.js`,
        dataType: 'json',
        data: {
          quantity,
          id: key,
        },
        success: (cart) => {
          if (this.updateTimeout !== thisTimeoutId) {
            return;
          }

          if (cart.item_count === 0) {
            // reload to show empty cart state
            window.location.reload();
            return;
          }

          this._updateCart(thisTimeoutId);
        },
      });
    }, 300);
  }

  /**
   * _updateCart
   *
   * Fetches new cart contents and swaps into page
   *
   * @param {integer} thisTimeoutId Id of timeout for this request. If no longer current, update is cancelled.
   */
  _updateCart(thisTimeoutId) {
    Asyncview.load(
      window.Theme.routes.cart_url,
      '_ajax',
    )
      .done(({ options, html }) => {
        // If another request is in progress, discard this update
        if (this.updateTimeout !== thisTimeoutId) {
          return;
        }

        this.cartMini.refresh();

        // Inject new cart table contents
        const newTableContainer = document.createElement('div');
        newTableContainer.innerHTML = html.table;

        morphdom(
          this.cartTable,
          newTableContainer.querySelector('table'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              // Skip images if src matches
              // - we don't want to reload lazy loaded images
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );

        rimg.watch(this.cartTable);

        // Inject new cart level discounts
        this.cartDiscounts.innerHTML = html.discounts;

        // Update total
        this.totalPrice.innerHTML = options.totalPrice;
      })
      .fail(() => window.location.reload());
  }

  saveNote() {
    const newNote = $('.instructions textarea', this.el).val();
    // empty function so that Shopify's callback isn't invoked
    Shopify.updateCartNote(newNote, () => {});
  }

  shippingCalculator() {
    Shopify.Cart.ShippingCalculator.show({
      submitButton: this.shippingContext.submitButton,
      submitButtonDisabled: this.shippingContext.submitButtonDisabled,
      customerIsLoggedIn: this.shippingContext.customerIsLoggedIn,
      moneyFormat: this.shippingContext.moneyFormat,
    });
  }

  calculateShipping(e) {
    if (e && (e.type === 'keydown')) {
      if (e.keyCode === 13) {
        e.preventDefault();
        $('.wrapper-response', this.el).empty();
      } else {
        return;
      }
    }

    const shippingAddress = {};
    shippingAddress.zip = $('.address-zip', this.el).val() || '';
    shippingAddress.country = $('.address-country', this.el).val() || '';
    shippingAddress.province = $('.address-province', this.el).val() || '';

    const queryString = Object.keys(shippingAddress)
      .map(key => `${encodeURIComponent(`shipping_address[${key}]`)}=${encodeURIComponent(shippingAddress[key])}`)
      .join('&');

    $.ajax(`/cart/shipping_rates.json?${queryString}`, { dataType: 'json' })
      .fail(error => this._handleErrors(error.responseJSON || {}))
      .done(response => {
        const rates = response.shipping_rates;

        let address = `${shippingAddress.zip}, ${shippingAddress.province}, ${shippingAddress.country}`;
        if (!shippingAddress.province.length) {
          address = `${shippingAddress.zip}, ${shippingAddress.country}`;
        }
        if (!shippingAddress.zip.length) {
          address = `${shippingAddress.province}, ${shippingAddress.country}`;
        }
        if (!shippingAddress.province.length || !shippingAddress.zip.length) {
          address = shippingAddress.country;
        }

        $('.wrapper-response', this.el)
          .empty()
          .append("<p class='shipping-calculator-response message'/><ul class='shipping-rates'/>");

        const ratesFeedback = $('.shipping-calculator-response', this.el);
        if (rates.length > 1) {
          const firstRate = Shopify.Cart.ShippingCalculator.formatRate(rates[0].price);
          const multipleShippingRates = Theme.shippingCalcMultiRates
            .replace('--address--', address)
            .replace('--number_of_rates--', rates.length)
            .replace('--rate--', `<span class='money'>${firstRate}</span>`);
          ratesFeedback.html(multipleShippingRates);
        } else if (rates.length === 1) {
          const oneShippingRate = Theme.shippingCalcOneRate
            .replace('--address--', address);
          ratesFeedback.html(oneShippingRate);
        } else {
          ratesFeedback.html(Theme.shippingCalcNoRates);
        }

        for (let i = 0; i < rates.length; i++) {
          const rate = rates[i];
          const price = Shopify.Cart.ShippingCalculator.formatRate(rate.price);
          const rateValues = Theme.shippingCalcRateValues
            .replace('--rate_title--', rate.name)
            .replace('--rate--', `<span class='money'>${price}</span>`);
          $('.shipping-rates', this.el).append(`<li>${rateValues}</li>`);
        }
      });
  }

  handleErrors(errors) {
    let errorMessage = $.parseJSON(errors.responseText);
    errorMessage = Theme.shippingCalcErrorMessage.replace('--error_message--', errorMessage.zip);
    return $('.wrapper-response', this.el).html(`<p class='message'>${errorMessage}</p>`);
  }

  onSectionUnload() {
    this.events.forEach(event => $(event).off('.cart'));

    this.cartForm.removeEventListener('click', this._handleClick);
    this.cartForm.removeEventListener('change', this._handleQtyChange);
    this.cartForm.removeEventListener('keydown', this._handleQtyKeydown);
  }
}
